import React, { useCallback, useRef } from "react";

import { datadogLogs } from "@datadog/browser-logs";
import {
  DonationFormCategory,
  Emptyable,
  ensureAbsoluteUrl,
  FormType,
  FrontendRoutes,
  FRAUDULENT_PAYMENT_INTENT,
  FormCategory,
  getDonationOrTicketingFormLink,
  OfferedFulfillmentMethod,
  ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE,
  PaymentMethod,
  sleep,
  TicketingFormCategory,
} from "@simplyk/common";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { PaymentMethod as StripePaymentMethod } from "@stripe/stripe-js";
import { inferRouterOutputs } from "@trpc/server";
import { UseFormGetValues, UseFormHandleSubmit, UseFormSetValue, UseFormTrigger } from "react-hook-form";
import { v4 } from "uuid";

import {
  OnPaymentSubmit,
  OnPaymentSucceeded,
  OnPayPalSubmit,
  OnPaymentSubmitResult,
} from "./StripePaymentComponentProps";
import type { StripePaymentOrganization } from "./types";

import {
  getCashAppConfirmationFailure,
  unknownPaymentErrorCode,
  validateAndExtractAddress,
  validateFulfillmentCheckoutForSubmit,
  withPaypalCapturedBuyer,
} from "@/canvas/features/FormV2/blocks/payment/PaymentSection/PaymentElement/helper";
import type { PaymentErrorContextType } from "@/canvas/features/FormV2/blocks/payment/PaymentSection/PaymentElement/PaymentErrorContext";
import { useStripePaymentReadiness } from "@/canvas/features/FormV2/blocks/payment/PaymentSection/PaymentElement/useStripePaymentReadiness";
import { sendEmbedLoadingMessage } from "@/canvas/features/FormV2/helpers/dynamicEmbedMessaging";
import {
  clearCashAppPaymentStarted,
  markCashAppPaymentStarted,
} from "@/canvas/features/PostFormSubmit/helpers/cashAppPaymentStarted";
import { buildPostFormSubmitUrl } from "@/canvas/features/PostFormSubmit/helpers/postFormSubmitUrl";
import { getAnimationNumber, TIME_BETWEEN_STEPS } from "@/canvas/features/ThankYouLoader/helpers";
import { isTest } from "@/common/constants/env";
import { RecaptchaAction } from "@/common/constants/recaptcha";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { getBillingDetails } from "@/common/helpers/payment";
import { getDeclineCodeFromServerError, PaymentMode } from "@/common/helpers/stripe";
import { navigateAfterPayment } from "@/common/helpers/thankYouPage";
import { runWithMinimumDuration } from "@/common/helpers/time";
import { AppRouterType, trpc } from "@/common/helpers/trpc";
import { useRecaptchaRetry } from "@/common/hooks/useRecaptchaRetry";
import { useTranslate } from "@/common/hooks/useTranslate";
import { hasRequiredSepaMandateConsent } from "@/common/helpers/payment-method";
import { DonationFormPaymentInput } from "@/common/types/donationForm";
import { TicketingPaymentInput } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { SubmitCommandFormInput } from "@/components/PaymentProcessor/helper";
type SubmitCommandOutput = inferRouterOutputs<AppRouterType>["form_submitCommand"];

const isSubmitCommandError = (r: OnPaymentSubmitResult): r is { error: string; code?: string; declineCode?: string } =>
  "error" in r && r.error !== undefined;

export type UseGenericPaymentSubmitParams = {
  handleSubmit: UseFormHandleSubmit<DonationFormPaymentInput | TicketingPaymentInput>;
  setValue: UseFormSetValue<DonationFormPaymentInput | TicketingPaymentInput>;
  /** Form path may use either Ticketing or Donation RHF instance; all paths are assignable to the union. */
  trigger: UseFormTrigger<DonationFormPaymentInput | TicketingPaymentInput>;
  getValues: UseFormGetValues<DonationFormPaymentInput | TicketingPaymentInput>;
  selectedPaymentMethod: PaymentMethod | undefined;
  setIsSubmitting: (v: boolean) => void;
  setIsPadLoading: (v: boolean) => void;
  paymentMode: PaymentMode;
  tipAmount: number;
  tipPercentage: number;
  organization: StripePaymentOrganization;
  commandId: string | null;
  isArchived: boolean;
  isDraft: boolean;
  onPaymentSubmit: OnPaymentSubmit;
  onPaymentSucceeded: OnPaymentSucceeded;
  onPayPalSubmit?: OnPayPalSubmit;
  formType: FormType;
  formCategory: TicketingFormCategory | DonationFormCategory;
  formData: { id: string; path: string };
  postTransactionUrl: string | null | undefined;
  hasADate: boolean;
  generateETicket: boolean;
  isEmbedV2: boolean;
  logDonorFormSubmitted: (args: { isExpressCheckout: boolean; isPaymentSucceeded: boolean }) => void;
  buildSubmitCommandFormInput: (args: {
    paymentInput: DonationFormPaymentInput | TicketingPaymentInput;
    paymentMethod: PaymentMethod;
    stripePaymentMethodId: string | null;
    cardBrand: string | null;
    tipAmount: number;
    tipPercentage: number;
    isExpressCheckout: boolean;
  }) => SubmitCommandFormInput;
  paymentErrorHandler: Pick<PaymentErrorContextType, "handlePaymentError" | "clearPaymentError">;
  requireFulfillmentCheckout: boolean;
  canOfferSameAsBilling: boolean;
  offeredMethods: OfferedFulfillmentMethod[];
};

export function useGenericPaymentSubmit({
  handleSubmit,
  setValue,
  trigger,
  getValues,
  selectedPaymentMethod,
  setIsSubmitting,
  setIsPadLoading,
  paymentMode,
  tipAmount,
  tipPercentage,
  organization,
  commandId,
  isArchived,
  isDraft,
  onPaymentSubmit,
  onPaymentSucceeded,
  onPayPalSubmit,
  formType,
  formCategory,
  formData,
  postTransactionUrl,
  hasADate,
  generateETicket,
  isEmbedV2,
  logDonorFormSubmitted,
  buildSubmitCommandFormInput,
  paymentErrorHandler,
  requireFulfillmentCheckout,
  canOfferSameAsBilling,
  offeredMethods,
}: UseGenericPaymentSubmitParams) {
  const { t } = useTranslate();
  const { isoLocale } = useLocaleContext();
  const stripe = useStripe();
  const elements = useElements();
  const { executeTrpc } = useRecaptchaRetry();
  const { handlePaymentError, clearPaymentError } = paymentErrorHandler;
  const ensureStripeReady = useStripePaymentReadiness(selectedPaymentMethod, handlePaymentError);
  const { mutateAsync: confirmPaymentIntentMutation } = trpc.form_confirmPaymentIntent.useMutation();
  const { mutateAsync: capturePaypalOrder } = trpc.paypalCaptureOrder.useMutation();
  const { mutateAsync: reOpenCommand } = trpc.reOpenCommand.useMutation();

  const submitDataRef = useRef<NonNullable<SubmitCommandOutput>["data"] | null>(null);
  const paymentInputRef = useRef<DonationFormPaymentInput | TicketingPaymentInput | null>(null);
  const isSubmitInFlightRef = useRef(false);

  const getFormErrors = useCallback(async (): Promise<string | null> => {
    if (isArchived) {
      return t("common", "form_archived");
    }
    if (isDraft) {
      return t("common", "error.form_draft");
    }
    if (Boolean(organization.blockPayment)) {
      return t("common", "payment_blocked");
    }
    if (!Boolean(organization.isStripeCustomAccountActive)) {
      return t("common", "bankIsNotConnectedError");
    }
    return null;
  }, [isArchived, isDraft, organization, t]);

  const payByStripe = async (
    paymentInput: DonationFormPaymentInput | TicketingPaymentInput,
    isExpressCheckout: boolean
    // eslint-disable-next-line sonarjs/cognitive-complexity
  ): Promise<string | undefined> => {
    const pm = selectedPaymentMethod!;
    const express = Boolean(isExpressCheckout);

    if (!stripe || !elements) {
      handlePaymentError({
        rawErrorCode: "stripe_load_error",
        rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.card1.code }),
        step: "stripe-load",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId,
        isKnownError: false,
      });
      return undefined;
    }

    const res = await elements?.submit();

    if (res.error) {
      elements.getElement("payment")?.focus();
      handlePaymentError({
        rawErrorCode: res.error.code || "stripe_element_submit_error",
        rawErrorMessage:
          res.error.message || t("common", "unknown_error", { code: unknownPaymentErrorCode.card1.code }),
        step: "stripe-element-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId,
        isKnownError: true,
      });
      return undefined;
    }

    const billing_details = getBillingDetails(paymentInput, organization);

    const paymentMethodResult = await createPaymentMethod({
      stripe,
      elements,
      billing_details,
    });

    if (paymentMethodResult.error) {
      handlePaymentError({
        rawErrorCode: paymentMethodResult.error.code || "create_payment_method_error",
        rawErrorMessage:
          paymentMethodResult.error.message ||
          t("common", "unknown_error", { code: unknownPaymentErrorCode.card1.code }),
        step: "create-payment-method",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId,
        isKnownError: true,
      });
      return undefined;
    }

    const stripePm = paymentMethodResult.paymentMethod;

    const submitResponse = await onPaymentSubmit({
      paymentInput,
      paymentMethod: pm,
      stripePaymentMethodId: stripePm.id,
      cardBrand: stripePm.card?.brand ?? null,
      tipAmount,
      tipPercentage,
      isExpressCheckout,
    });

    if (isSubmitCommandError(submitResponse)) {
      handlePaymentError({
        rawErrorCode: submitResponse.code || "server_submit_error",
        rawErrorMessage:
          submitResponse.code === ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE
            ? ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE
            : submitResponse.error,
        step: "server-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId,
        isKnownError: true,
        declineCode: submitResponse.declineCode,
      });
      return undefined;
    }

    const clientSecret = submitResponse.clientSecret;
    const command = submitResponse.command;
    const transaction = submitResponse.transaction ?? null;
    const emailUpdateToken = submitResponse.emailUpdateToken;
    const buyerTicketToken = submitResponse.buyerTicketToken;

    if (clientSecret === FRAUDULENT_PAYMENT_INTENT.clientSecret) {
      const result = await onPaymentSucceeded({
        command,
        transaction,
        formType,
        paymentInput,
        isExpressCheckout,
        emailUpdateToken,
        buyerTicketToken,
      });
      if (result.error || !result.returnUrl) {
        handlePaymentError({
          rawErrorCode: result.error ? "post_submit_error" : "missing_return_url",
          rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.card2.code }),
          step: "post-submit",
          paymentMethod: pm,
          isExpressCheckout: express,
          commandId: command?.id,
          isKnownError: false,
        });
        return undefined;
      }
      return result.returnUrl;
    }

    if (!command) {
      handlePaymentError({
        rawErrorCode: "missing_command",
        rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.card3.code }),
        step: "server-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId,
        isKnownError: false,
      });
      return undefined;
    }

    if (clientSecret && !isTest) {
      try {
        const { error, paymentIntent, setupIntent } = await stripe.handleNextAction({ clientSecret });
        if (error) {
          handlePaymentError({
            rawErrorCode: error.code || "handle_next_action_error",
            rawErrorMessage:
              error.message || t("common", "unknown_error", { code: unknownPaymentErrorCode.card4.code }),
            step: "handle-next-action",
            paymentMethod: pm,
            isExpressCheckout: express,
            commandId: command.id,
            isKnownError: true,
            declineCode: error.decline_code,
          });
          return undefined;
        }

        if (!paymentIntent && !setupIntent) {
          handlePaymentError({
            rawErrorCode: "missing_intent",
            rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.card4.code }),
            step: "handle-next-action",
            paymentMethod: pm,
            isExpressCheckout: express,
            commandId: command.id,
            isKnownError: false,
          });
          return undefined;
        }

        if (paymentIntent && paymentIntent.status === "requires_payment_method") {
          handlePaymentError({
            rawErrorCode: paymentIntent.last_payment_error?.code || "requires_payment_method",
            rawErrorMessage:
              paymentIntent.last_payment_error?.message ||
              t("common", "unknown_error", { code: unknownPaymentErrorCode.card5.code }),
            step: "handle-next-action",
            paymentMethod: pm,
            isExpressCheckout: express,
            commandId: command.id,
            isKnownError: Boolean(paymentIntent.last_payment_error),
            declineCode: paymentIntent.last_payment_error?.decline_code,
          });
          return undefined;
        }

        if (paymentIntent && paymentIntent.status === "requires_confirmation") {
          const action = RecaptchaAction.ConfirmPaymentIntent;
          const email = command.email;
          const { data, error } = await executeTrpc(action, email, confirmPaymentIntentMutation, {
            paymentIntentId: paymentIntent.id,
          });

          if (error || !data) {
            const code = error?.code || "unknown_error";

            const unknownGenericErrorTraduction = t("common", "unknown_error", {
              code: unknownPaymentErrorCode.card6.code,
            });

            const displayedMessage =
              code === "confirm_payment_intent_failed"
                ? error?.message
                : t("common", `error.${code as never}`) === code
                  ? unknownGenericErrorTraduction
                  : t("common", `error.${code as never}`);

            handlePaymentError({
              rawErrorCode: code,
              rawErrorMessage: displayedMessage || unknownGenericErrorTraduction,
              step: "manual-confirmation",
              paymentMethod: pm,
              isExpressCheckout: express,
              commandId: command.id,
              isKnownError: code === "confirm_payment_intent_failed",
              declineCode: getDeclineCodeFromServerError(error),
            });
            return undefined;
          }
        }
      } catch (error) {
        const caughtMessage = error instanceof Error ? error.message : String(error);
        handlePaymentError({
          rawErrorCode: `caught_exception  – ${caughtMessage}`,
          rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.card7.code }),
          step: "handle-next-action",
          paymentMethod: pm,
          isExpressCheckout: express,
          commandId: command.id,
          isKnownError: false,
        });
        return undefined;
      }
    }

    const result = await onPaymentSucceeded({
      paymentInput,
      command,
      transaction,
      formType,
      isExpressCheckout,
      emailUpdateToken,
      buyerTicketToken,
    });

    if (result.returnUrl) {
      return result.returnUrl;
    }

    if (result.error) {
      handlePaymentError({
        rawErrorCode: "post_submit_error",
        rawErrorMessage: result.error,
        step: "post-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId: command.id,
        isPaymentSucceeded: true,
        isKnownError: false,
      });
    } else {
      handlePaymentError({
        rawErrorCode: "missing_return_url",
        rawErrorMessage: t("common", "unknown_error.paymentSucceeded", { code: unknownPaymentErrorCode.card8.code }),
        step: "post-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId: command.id,
        isPaymentSucceeded: true,
        isKnownError: false,
      });
    }
    return undefined;
  };

  const payByPad = async (
    paymentInput: DonationFormPaymentInput | TicketingPaymentInput,
    isExpressCheckout: boolean,
    minimumDuration: number
  ): Promise<string | undefined> => {
    const express = Boolean(isExpressCheckout);

    if (!stripe) {
      handlePaymentError({
        rawErrorCode: "stripe_load_error",
        rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.pad2.code }),
        step: "stripe-load",
        paymentMethod: PaymentMethod.Pad,
        isExpressCheckout: express,
        commandId,
        isKnownError: false,
      });
      return undefined;
    }

    const submitResponse = await onPaymentSubmit({
      paymentInput,
      paymentMethod: PaymentMethod.Pad,
      stripePaymentMethodId: null,
      cardBrand: null,
      tipAmount,
      tipPercentage,
      isExpressCheckout,
    });

    if (isSubmitCommandError(submitResponse)) {
      handlePaymentError({
        rawErrorCode: submitResponse.code || "server_submit_error",
        rawErrorMessage:
          submitResponse.code === ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE
            ? ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE
            : submitResponse.error,
        step: "server-submit",
        paymentMethod: PaymentMethod.Pad,
        isExpressCheckout: express,
        commandId,
        isKnownError: true,
      });
      return undefined;
    }

    const clientSecret = submitResponse.clientSecret;
    const command = submitResponse.command;
    const transaction = submitResponse.transaction ?? null;
    const emailUpdateToken = submitResponse.emailUpdateToken;
    const buyerTicketToken = submitResponse.buyerTicketToken;

    if (clientSecret === FRAUDULENT_PAYMENT_INTENT.clientSecret) {
      const result = await onPaymentSucceeded({
        command,
        transaction,
        formType,
        paymentInput,
        isExpressCheckout,
        emailUpdateToken,
        buyerTicketToken,
      });
      if (result.error || !result.returnUrl) {
        handlePaymentError({
          rawErrorCode: result.error ? "post_submit_error" : "missing_return_url",
          rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.pad1.code }),
          step: "post-submit",
          paymentMethod: PaymentMethod.Pad,
          isExpressCheckout: express,
          commandId: command?.id,
          isKnownError: false,
        });
        return undefined;
      }
      return result.returnUrl;
    }

    if (!clientSecret || !command) {
      handlePaymentError({
        rawErrorCode: !clientSecret ? "missing_client_secret" : "missing_command",
        rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.pad2.code }),
        step: "server-submit",
        paymentMethod: PaymentMethod.Pad,
        isExpressCheckout: express,
        commandId,
        isKnownError: false,
      });
      return undefined;
    }

    let confirmError: import("@stripe/stripe-js").StripeError | undefined;
    if (paymentMode === "setup") {
      const { error } = await stripe.confirmAcssDebitSetup(clientSecret, {
        payment_method: {
          billing_details: getBillingDetails(paymentInput, organization),
        },
        mandate_data: {
          acss_debit: {
            mandate_options: {
              payment_schedule: "interval",
              interval_description: "on completion of checkout",
              transaction_type: "personal",
            },
          },
        },
      });
      confirmError = error;
    } else {
      const { error } = await stripe.confirmAcssDebitPayment(clientSecret, {
        payment_method: {
          billing_details: getBillingDetails(paymentInput, organization),
        },
      });
      confirmError = error;
    }

    if (confirmError) {
      handlePaymentError({
        rawErrorCode: confirmError.code || "pad_confirm_error",
        rawErrorMessage:
          confirmError.message || t("common", "unknown_error", { code: unknownPaymentErrorCode.pad3.code }),
        step: "manual-confirmation",
        paymentMethod: PaymentMethod.Pad,
        isExpressCheckout: express,
        commandId,
        isKnownError: true,
      });
      return undefined;
    }

    const result = await onPaymentSucceeded({
      paymentInput,
      command,
      transaction,
      formType,
      isExpressCheckout,
      emailUpdateToken,
      buyerTicketToken,
    });

    if (result?.returnUrl) {
      setIsSubmitting(true);
      await sleep(minimumDuration);
      return result.returnUrl;
    }

    if (result?.error) {
      handlePaymentError({
        rawErrorCode: "post_submit_error",
        rawErrorMessage: result.error,
        step: "post-submit",
        paymentMethod: PaymentMethod.Pad,
        isExpressCheckout: express,
        commandId: command.id,
        isPaymentSucceeded: true,
        isKnownError: false,
      });
    } else {
      handlePaymentError({
        rawErrorCode: "missing_return_url",
        rawErrorMessage: t("common", "unknown_error.paymentSucceeded", { code: unknownPaymentErrorCode.pad4.code }),
        step: "post-submit",
        paymentMethod: PaymentMethod.Pad,
        isExpressCheckout: express,
        commandId: command.id,
        isPaymentSucceeded: true,
        isKnownError: false,
      });
    }
    return undefined;
  };

  const payByCashapp = async (
    paymentInput: DonationFormPaymentInput | TicketingPaymentInput,
    isExpressCheckout: boolean
  ): Promise<string | undefined> => {
    const express = Boolean(isExpressCheckout);

    if (!stripe) {
      handlePaymentError({
        rawErrorCode: "stripe_load_error",
        rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.cashApp1.code }),
        step: "stripe-load",
        paymentMethod: PaymentMethod.CashApp,
        isExpressCheckout: express,
        commandId,
        isKnownError: false,
      });
      return undefined;
    }

    const submitResponse = await onPaymentSubmit({
      paymentInput,
      paymentMethod: PaymentMethod.CashApp,
      stripePaymentMethodId: null,
      cardBrand: null,
      tipAmount,
      tipPercentage,
      isExpressCheckout,
    });

    if (isSubmitCommandError(submitResponse)) {
      handlePaymentError({
        rawErrorCode: submitResponse.code || "server_submit_error",
        rawErrorMessage:
          submitResponse.code === ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE
            ? ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE
            : submitResponse.error,
        step: "server-submit",
        paymentMethod: PaymentMethod.CashApp,
        isExpressCheckout: express,
        commandId,
        isKnownError: true,
      });
      return undefined;
    }

    const clientSecret = submitResponse.clientSecret;
    const command = submitResponse.command;
    const emailUpdateToken = submitResponse.emailUpdateToken;
    const buyerTicketToken = submitResponse.buyerTicketToken;
    if (!clientSecret || !command) {
      handlePaymentError({
        rawErrorCode: !clientSecret ? "missing_client_secret" : "missing_command",
        rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.cashApp1.code }),
        step: "server-submit",
        paymentMethod: PaymentMethod.CashApp,
        isExpressCheckout: express,
        commandId,
        isKnownError: false,
      });
      return undefined;
    }

    const returnUrl =
      submitResponse.cashAppReturnUrl ??
      buildPostFormSubmitUrl({
        commandId: commandId ?? command.id,
        formId: formData.id,
        formType,
        formCategory,
        isoLocale,
        thankYouUrl: "",
        formUrl: getDonationOrTicketingFormLink({
          form: {
            id: formData.id,
            path: formData.path,
            formType,
            formCategory: formCategory as FormCategory,
            isPrimaryCampaign: undefined,
            isPrimaryTeam: undefined,
          },
          isoLocale,
        }),
        organizationName: organization.name,
        hasADate,
        generateETicket: generateETicket && command.productsAmount > 0,
        generateTaxReceipt: (command.eligibleAmount || 0) > 0 && (organization.canGenerateReceipt ?? false),
        organizationId: organization.id,
        transactionId: submitResponse.transaction?.id,
        productsAmount: command.productsAmount,
        extraDonation: command.extraDonation,
        currency: command.currency,
      });
    const confirmationOptions = {
      return_url: returnUrl,
      payment_method: {
        billing_details: getBillingDetails(paymentInput, organization),
      },
    };
    markCashAppPaymentStarted(returnUrl);
    const confirmationResult =
      paymentMode === "setup"
        ? await stripe.confirmCashappSetup(clientSecret, confirmationOptions)
        : await stripe.confirmCashappPayment(clientSecret, confirmationOptions);
    clearCashAppPaymentStarted(returnUrl);
    const confirmationFailure = getCashAppConfirmationFailure(confirmationResult);

    if (confirmationFailure) {
      handlePaymentError({
        rawErrorCode: confirmationFailure.code,
        rawErrorMessage: confirmationFailure.message || t("common", "payment.cashApp.notCompleted.title"),
        step: "manual-confirmation",
        paymentMethod: PaymentMethod.CashApp,
        isExpressCheckout: express,
        commandId,
        isKnownError: true,
      });
      return undefined;
    }

    const result = await onPaymentSucceeded({
      paymentInput,
      command,
      transaction: submitResponse.transaction ?? null,
      formType,
      isExpressCheckout,
      emailUpdateToken,
      buyerTicketToken,
    });
    if (result?.error) {
      handlePaymentError({
        rawErrorCode: "post_submit_error",
        rawErrorMessage: result.error,
        step: "post-submit",
        paymentMethod: PaymentMethod.CashApp,
        isExpressCheckout: express,
        commandId: command.id,
        isPaymentSucceeded: true,
        isKnownError: false,
      });
      return undefined;
    }
    return result?.returnUrl ?? undefined;
  };

  const payOffline = async (
    paymentInput: DonationFormPaymentInput | TicketingPaymentInput,
    pm: PaymentMethod,
    isExpressCheckout: boolean
  ): Promise<string | undefined> => {
    const express = Boolean(isExpressCheckout);

    const submitResponse = await onPaymentSubmit({
      paymentInput,
      paymentMethod: pm,
      stripePaymentMethodId: null,
      cardBrand: null,
      tipAmount,
      tipPercentage,
      isExpressCheckout,
    });

    if (isSubmitCommandError(submitResponse)) {
      handlePaymentError({
        rawErrorCode: submitResponse.code || "server_submit_error",
        rawErrorMessage:
          submitResponse.code === ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE
            ? ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE
            : submitResponse.error,
        step: "server-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId,
        isKnownError: true,
      });
      return undefined;
    }

    const command = submitResponse.command;
    const transaction = submitResponse.transaction ?? null;
    const emailUpdateToken = submitResponse.emailUpdateToken;
    const buyerTicketToken = submitResponse.buyerTicketToken;
    if (!command) {
      handlePaymentError({
        rawErrorCode: "missing_command",
        rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.offline1.code }),
        step: "server-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId,
        isKnownError: false,
      });
      return undefined;
    }

    const result = await onPaymentSucceeded({
      paymentInput,
      command,
      transaction,
      formType,
      emailUpdateToken,
      buyerTicketToken,
    });
    if (result.returnUrl) {
      return result.returnUrl;
    }

    if (result.error) {
      handlePaymentError({
        rawErrorCode: "post_submit_error",
        rawErrorMessage: result.error,
        step: "post-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId: command.id,
        isPaymentSucceeded: true,
        isKnownError: false,
      });
    } else {
      handlePaymentError({
        rawErrorCode: "missing_return_url",
        rawErrorMessage: t("common", "unknown_error.paymentSucceeded", { code: unknownPaymentErrorCode.offline2.code }),
        step: "post-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId: command.id,
        isPaymentSucceeded: true,
        isKnownError: false,
      });
    }
    return undefined;
  };

  const paypalSubmit = onPayPalSubmit ?? onPaymentSubmit;

  const createPaypalOrderGeneric = async (
    paymentMethod: PaymentMethod.PayPal | PaymentMethod.Venmo
  ): Promise<string | null> => {
    const submitCommandInput = await prepareSubmit(paymentMethod);
    if (!submitCommandInput) {
      return null;
    }

    if (commandId) {
      try {
        await reOpenCommand({ commandId });
      } catch {
        datadogLogs.logger.info("paypal.reopen.skipped", { commandId, paymentMethod });
      }
    }

    datadogLogs.logger.info("paypal.submit.started", { commandId, paymentMethod });

    const submitResponse = await paypalSubmit({
      paymentInput: submitCommandInput.paymentInput,
      paymentMethod,
      stripePaymentMethodId: null,
      cardBrand: null,
      tipAmount: submitCommandInput.tip,
      tipPercentage: submitCommandInput.tipPercentage,
      isExpressCheckout: Boolean(submitCommandInput.isExpressCheckout),
    });

    if (isSubmitCommandError(submitResponse)) {
      datadogLogs.logger.error("paypal.submit.failed", {
        commandId,
        errorCode: submitResponse.code,
        errorMessage: submitResponse.error,
      });
      handlePaymentError({
        rawErrorCode: submitResponse.code ?? "paypal_submit_error",
        rawErrorMessage: submitResponse.error,
        step: "server-submit",
        paymentMethod,
        isExpressCheckout: false,
        commandId,
        isKnownError: true,
      });
      return null;
    }

    submitDataRef.current = submitResponse;
    paymentInputRef.current = submitCommandInput.paymentInput;

    const orderId = submitResponse.paypalOrderId;
    if (!orderId) {
      datadogLogs.logger.error("paypal.order.failed", { commandId, errorCode: "missing_paypal_order_id" });
      handlePaymentError({
        rawErrorCode: "missing_paypal_order_id",
        rawErrorMessage: "No PayPal order ID returned from server",
        step: "server-submit",
        paymentMethod,
        isExpressCheckout: false,
        commandId,
        isKnownError: true,
      });
      return null;
    }

    datadogLogs.logger.info("paypal.order.created", { commandId, orderId });
    return orderId;
  };

  const capturePaypalGeneric = async ({ orderId }: { orderId: string }): Promise<void> => {
    datadogLogs.logger.info("paypal.capture.started", { commandId, orderId });
    setIsSubmitting(true);
    const minimumDuration = TIME_BETWEEN_STEPS * 1000 * getAnimationNumber({ isFree: false });

    const returnUrl = await runWithMinimumDuration<string | null>(async () => {
      const response = await executeTrpc(RecaptchaAction.PaypalCaptureOrder, undefined, capturePaypalOrder, {
        orderId,
        commandId: commandId || "",
      });

      if (response?.error) {
        setIsSubmitting(false);
        datadogLogs.logger.error("paypal.capture.failed", {
          commandId,
          orderId,
          errorCode: response.error.code,
          errorMessage: response.error.message,
        });
        handlePaymentError({
          rawErrorCode: response.error.code ?? "paypal_capture_error",
          rawErrorMessage: response.error.message ?? "PayPal capture failed",
          step: "server-submit",
          paymentMethod: PaymentMethod.PayPal,
          isExpressCheckout: false,
          commandId,
          isKnownError: true,
        });
        return { callbackResult: null, shouldAwaitMinimumDuration: false };
      }

      const submitData = submitDataRef.current;
      const paymentInput = paymentInputRef.current;
      if (!submitData?.command || !formType || !paymentInput || !submitData.transaction) {
        return { callbackResult: ensureAbsoluteUrl(postTransactionUrl) ?? null, shouldAwaitMinimumDuration: true };
      }

      const result = await onPaymentSucceeded({
        paymentInput,
        command: withPaypalCapturedBuyer(submitData.command, response?.data),
        transaction: submitData.transaction,
        formType,
        emailUpdateToken: submitData.emailUpdateToken,
        buyerTicketToken: submitData.buyerTicketToken,
      });

      datadogLogs.logger.info("paypal.capture.succeeded", { commandId, orderId });
      return {
        callbackResult: result.returnUrl ?? ensureAbsoluteUrl(postTransactionUrl) ?? null,
        shouldAwaitMinimumDuration: true,
      };
    }, minimumDuration);

    if (returnUrl) {
      await navigateAfterPayment(returnUrl);
    }
  };

  const onInvalid = useCallback(
    async (isExpressCheckout: boolean) => {
      const addressElement = elements?.getElement?.("address");
      await addressElement?.getValue?.();
      if (
        selectedPaymentMethod &&
        selectedPaymentMethod !== PaymentMethod.PayPal &&
        selectedPaymentMethod !== PaymentMethod.Venmo
      ) {
        await elements?.submit();
      }

      logDonorFormSubmitted({ isExpressCheckout, isPaymentSucceeded: false });
    },
    [elements, selectedPaymentMethod, logDonorFormSubmitted]
  );

  const handleFormSubmit = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    (isExpressCheckout = false) =>
      async (event?: React.SyntheticEvent): Promise<boolean> => {
        event && event.preventDefault();

        if (isSubmitInFlightRef.current) {
          return false;
        }
        if (!ensureStripeReady(isExpressCheckout)) {
          return false;
        }
        isSubmitInFlightRef.current = true;

        clearPaymentError();

        let paymentSucceeded = false;

        await handleSubmit(
          async (paymentInput: DonationFormPaymentInput | TicketingPaymentInput) => {
            if (!selectedPaymentMethod) {
              return;
            }

            if (!hasRequiredSepaMandateConsent(selectedPaymentMethod, paymentInput.sepaMandateAccepted)) {
              handlePaymentError({
                rawErrorCode: "sepa_mandate_not_accepted",
                rawErrorMessage: t("common", "payment.sepa.consentRequired"),
                step: "form-validation",
                paymentMethod: selectedPaymentMethod,
                isExpressCheckout,
                commandId,
                isKnownError: true,
              });
              return;
            }

            if (selectedPaymentMethod !== PaymentMethod.Pad) {
              setIsSubmitting(true);
            } else {
              setIsPadLoading(true);
            }

            const minimumDuration =
              selectedPaymentMethod !== PaymentMethod.CashApp
                ? TIME_BETWEEN_STEPS * 1000 * getAnimationNumber({ isFree: false })
                : 0;
            const url = await runWithMinimumDuration<Emptyable<string>>(async () => {
              const formError = await getFormErrors();
              if (formError) {
                handlePaymentError({
                  rawErrorCode: "form_validation_error",
                  rawErrorMessage: formError,
                  step: "form-validation",
                  paymentMethod: selectedPaymentMethod,
                  isExpressCheckout,
                  commandId,
                  isKnownError: true,
                });
                return { callbackResult: null, shouldAwaitMinimumDuration: false };
              }

              const addressValidationResult = await validateAndExtractAddress({
                elements,
                paymentInput,
                t,
                handlePaymentError,
                selectedPaymentMethod,
                isExpressCheckout,
                commandId,
                setValue,
              });

              if (addressValidationResult.shouldEarlyReturn) {
                return { callbackResult: null, shouldAwaitMinimumDuration: false };
              }

              setValue("country", paymentInput.country);
              setValue("region", paymentInput.region);
              if (!(await trigger("raffleEligibility"))) {
                return { callbackResult: null, shouldAwaitMinimumDuration: false };
              }

              if (
                validateFulfillmentCheckoutForSubmit({
                  paymentInput,
                  formType,
                  t,
                  handlePaymentError,
                  selectedPaymentMethod,
                  isExpressCheckout,
                  commandId,
                  requireFulfillmentCheckout,
                  canOfferSameAsBilling,
                  offeredMethods,
                })
              ) {
                return { callbackResult: null, shouldAwaitMinimumDuration: false };
              }

              if ((tipAmount && tipAmount < 0) || !organization?.country || !formType) {
                handlePaymentError({
                  rawErrorCode: "invalid_form_state",
                  rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.other2.code }),
                  step: "form-validation",
                  paymentMethod: selectedPaymentMethod,
                  isExpressCheckout,
                  commandId,
                  isKnownError: false,
                });
                return { callbackResult: null, shouldAwaitMinimumDuration: false };
              }

              if (selectedPaymentMethod === PaymentMethod.Pad) {
                return {
                  callbackResult: await payByPad(paymentInput, isExpressCheckout, minimumDuration),
                  shouldAwaitMinimumDuration: false,
                };
              }
              if (selectedPaymentMethod === PaymentMethod.CashApp) {
                return {
                  callbackResult: await payByCashapp(paymentInput, isExpressCheckout),
                  shouldAwaitMinimumDuration: false,
                };
              }

              if (selectedPaymentMethod === PaymentMethod.Cheque || selectedPaymentMethod === PaymentMethod.Cash) {
                return {
                  callbackResult: await payOffline(paymentInput, selectedPaymentMethod, isExpressCheckout),
                  shouldAwaitMinimumDuration: true,
                };
              }

              if (selectedPaymentMethod === PaymentMethod.PayPal || selectedPaymentMethod === PaymentMethod.Venmo) {
                return { callbackResult: null, shouldAwaitMinimumDuration: false };
              }

              return {
                callbackResult: await payByStripe(paymentInput, isExpressCheckout),
                shouldAwaitMinimumDuration: true,
              };
            }, minimumDuration);
            if (url) {
              paymentSucceeded = true;
              if (isEmbedV2 && url.includes(FrontendRoutes.EmbedThankYou)) {
                sendEmbedLoadingMessage();
              }
              try {
                await navigateAfterPayment(url);
              } catch (redirectError) {
                handlePaymentError({
                  rawErrorCode: "thank_you_redirect_failed",
                  rawErrorMessage: redirectError instanceof Error ? redirectError.message : String(redirectError),
                  step: "post-submit",
                  paymentMethod: selectedPaymentMethod,
                  isExpressCheckout,
                  isPaymentSucceeded: true,
                  isKnownError: false,
                });
              }
            }
          },
          () => onInvalid(isExpressCheckout)
        )().finally(() => {
          isSubmitInFlightRef.current = false;
          setIsSubmitting(false);
          setIsPadLoading(false);
        });

        return paymentSucceeded;
      },
    // Inner payBy* functions are not useCallback-wrapped (mirrors legacy payment hooks).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- payByPad, payByCashapp, payOffline, payByStripe
    [
      handleSubmit,
      ensureStripeReady,
      trigger,
      selectedPaymentMethod,
      setIsSubmitting,
      setIsPadLoading,
      getFormErrors,
      elements,
      tipAmount,
      organization?.country,
      formType,
      handlePaymentError,
      t,
      commandId,
      onInvalid,
      clearPaymentError,
      isEmbedV2,
    ]
  );

  const prepareSubmit = useCallback(
    async (paymentMethod: PaymentMethod): Promise<SubmitCommandFormInput | null> => {
      const isValid = await trigger(undefined, { shouldFocus: true });
      if (!isValid) {
        return null;
      }

      const paymentInput = getValues() as DonationFormPaymentInput | TicketingPaymentInput;

      const formError = await getFormErrors();
      if (formError) {
        handlePaymentError({
          rawErrorCode: "form_validation_error",
          rawErrorMessage: formError,
          step: "form-validation",
          paymentMethod,
          isExpressCheckout: false,
          commandId,
          isKnownError: true,
        });
        return null;
      }

      const addressValidationResult = await validateAndExtractAddress({
        elements,
        paymentInput,
        t,
        handlePaymentError,
        selectedPaymentMethod: paymentMethod,
        isExpressCheckout: false,
        commandId,
        setValue,
      });

      if (addressValidationResult.shouldEarlyReturn) {
        return null;
      }

      setValue("country", paymentInput.country);
      setValue("region", paymentInput.region);
      if (!(await trigger("raffleEligibility"))) {
        return null;
      }

      if (
        formType &&
        validateFulfillmentCheckoutForSubmit({
          paymentInput,
          formType,
          t,
          handlePaymentError,
          selectedPaymentMethod: paymentMethod,
          isExpressCheckout: false,
          commandId,
          requireFulfillmentCheckout,
          canOfferSameAsBilling,
          offeredMethods,
        })
      ) {
        return null;
      }

      if ((tipAmount && tipAmount < 0) || !organization?.country || !formType) {
        handlePaymentError({
          rawErrorCode: "invalid_form_state",
          rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.other2.code }),
          step: "form-validation",
          paymentMethod,
          isExpressCheckout: false,
          commandId,
          isKnownError: false,
        });
        return null;
      }

      return buildSubmitCommandFormInput({
        paymentInput,
        paymentMethod,
        stripePaymentMethodId: null,
        cardBrand: null,
        tipAmount,
        tipPercentage,
        isExpressCheckout: false,
      });
    },
    [
      trigger,
      getValues,
      getFormErrors,
      handlePaymentError,
      commandId,
      elements,
      t,
      tipAmount,
      tipPercentage,
      organization?.country,
      formType,
      buildSubmitCommandFormInput,
      requireFulfillmentCheckout,
      canOfferSameAsBilling,
      offeredMethods,
      setValue,
    ]
  );

  return { handleFormSubmit, prepareSubmit, createPaypalOrderGeneric, capturePaypalGeneric };
}

type CreatePaymentMethodParams = {
  stripe: ReturnType<typeof useStripe>;
  elements: ReturnType<typeof useElements>;
  billing_details: ReturnType<typeof getBillingDetails>;
};

type CreatePaymentMethodResult =
  | { paymentMethod: StripePaymentMethod; error?: undefined }
  | { paymentMethod?: undefined; error: { message?: string; code?: string } };

const createPaymentMethod = async ({
  stripe,
  elements,
  billing_details,
}: CreatePaymentMethodParams): Promise<CreatePaymentMethodResult> => {
  if (isTest) {
    return {
      paymentMethod: {
        id: `pm_test_${v4()}`,
      } as StripePaymentMethod,
    };
  }

  const { error, paymentMethod } = await stripe!.createPaymentMethod({
    elements: elements!,
    params: {
      billing_details,
    },
  });

  if (error) {
    return { error };
  }

  return { paymentMethod };
};
